/**
 * Utilitários puros para a parametrização de workflow.
 * Funções sem dependência de `this`/Vue para reuso entre componentes.
 */

/**
 * Normaliza o payload de encaminhamentos vindo do backend
 * para o formato interno (com `tipo_destino`).
 */
export function normalizeEncaminhamentos(encaminhamentos) {
  if (!encaminhamentos) {
    return [];
  }

  const normalizarItem = enc => {
    const final = enc.final || null;

    return {
      nome: enc.nome || "",
      tipo_destino: final
        ? `final_${final}`
        : enc.tipo_destino || "etapa",
      workflow_id:
        enc.workflow_id != null && !final ? enc.workflow_id : null,
      status_id:
        enc.status_id != null && !final ? enc.status_id : null
    };
  };

  if (Array.isArray(encaminhamentos)) {
    return encaminhamentos.map(enc => normalizarItem(enc));
  }

  if (typeof encaminhamentos === "object") {
    return [normalizarItem(encaminhamentos)];
  }

  return [];
}

/**
 * Encaminhamentos de um status sempre como array (aceita `null`, array ou
 * objeto único). Retorna os itens **crus** — preservando `workflow_id`/`final`
 * exatamente como o backend armazena (usado pelos grafos e validações).
 *
 * @param {Object} status
 * @returns {Array<Object>}
 */
export function listarEncaminhamentos(status) {
  const encaminhamentos = status && status.encaminhamentos;

  if (Array.isArray(encaminhamentos)) {
    return encaminhamentos;
  }

  if (encaminhamentos && typeof encaminhamentos === "object") {
    return [encaminhamentos];
  }

  return [];
}

/**
 * Converte um encaminhamento interno (com `tipo_destino`)
 * no payload esperado pelo backend.
 */
export function encaminhamentoPayload(enc) {
  if (!enc) {
    return null;
  }

  if (enc.tipo_destino === "final_aprovado") {
    return { nome: enc.nome, final: "aprovado" };
  }

  if (enc.tipo_destino === "final_reprovado") {
    return { nome: enc.nome, final: "reprovado" };
  }

  if (enc.workflow_id && enc.status_id) {
    return {
      nome: enc.nome,
      workflow_id: enc.workflow_id,
      status_id: enc.status_id
    };
  }

  return null;
}

/**
 * Texto legível de um encaminhamento.
 * `etapas` é usado para resolver o nome da etapa/status destino.
 */
export function encaminhamentoText(enc, etapas) {
  if (!enc) {
    return "Sem encaminhamentos";
  }

  if (typeof enc !== "object") {
    return String(enc);
  }

  if (enc.final === "aprovado" || enc.tipo_destino === "final_aprovado") {
    return "Aprovação final";
  }

  if (enc.final === "reprovado" || enc.tipo_destino === "final_reprovado") {
    return "Reprovação final";
  }

  const listaEtapas = Array.isArray(etapas) ? etapas : [];
  const etapa = listaEtapas.find(e => e.id === enc.workflow_id);
  const etapaNome = etapa ? etapa.nome : `Etapa #${enc.workflow_id}`;

  let statusNome = null;

  if (etapa && Array.isArray(etapa.status)) {
    const status = etapa.status.find(s => s.status_id === enc.status_id);

    if (status && status.tipo) {
      statusNome = status.tipo.nome || status.tipo.slug;
    }
  }

  if (!statusNome || statusNome === etapaNome) {
    return etapaNome;
  }

  return `${statusNome} de ${etapaNome}`;
}

/** Rótulo de SLA (dias úteis). */
export function slaLabel(value) {
  if (value == null || value === "") {
    return "Indefinido";
  }

  if (Number(value) === 0) {
    return "Mesmo dia";
  }

  return `${value} dia(s) útil(is)`;
}

/** Rótulo de seleção de revisores. */
export function revisoresLabel(value) {
  return value ? "Revisores selecionados" : "Revisores se voluntariam";
}

/** Nome legível de um status (usa `tipo.nome` com fallback). */
export function statusNome(status) {
  if (status && status.tipo && status.tipo.nome) {
    return status.tipo.nome;
  }

  return `Status #${status ? status.status_id : "?"}`;
}

/** Converte hex `#rrggbb` em rgba com alpha fixo 0.45. */
export function tableBorderColor(hex) {
  return rgbaFromHex(hex, 0.45);
}

/** Converte hex `#rrggbb` em cor sólida escurecida (80%). */
export function tableChipColor(hex) {
  return darkenRgb(hex, 0.8);
}

/** Converte hex `#rrggbb` em cor sólida mais escura (60%). */
export function tableActionColor(hex) {
  return darkenRgb(hex, 0.6);
}

function darkenRgb(hex, factor) {
  const rgb = rgbComponents(hex);

  if (!rgb) {
    return hex;
  }

  const r = Math.round(rgb.r * factor);
  const g = Math.round(rgb.g * factor);
  const b = Math.round(rgb.b * factor);

  return `rgb(${r}, ${g}, ${b})`;
}

function rgbaFromHex(hex, alpha) {
  const rgb = rgbComponents(hex);

  if (!rgb) {
    return hex;
  }

  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

function rgbComponents(hex) {
  if (
    typeof hex !== "string" ||
    !hex.startsWith("#") ||
    hex.length !== 7
  ) {
    return null;
  }

  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16)
  };
}